import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  ButtonGroup,
  EmptyState,
  IndexTable,
  Layout,
  LegacyCard,
  Spinner,
  Text,
} from '@shopify/polaris';
import { PrintMinor, RefreshMinor } from '@shopify/polaris-icons';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';
import { saveBlobDownload } from '../services/montageJobsService';
import { printingsService } from '../services/printingsService';

const SERVER_URL = getServerURL();

const EXPRESS_TYPE_CONFIG = {
  booklet_express: {
    label: 'Booklet Express',
    innerLabel: 'Story PDF',
    endpoint: 'montagedpdf-booklet',
    urlField: 'pdf_montaged_booklet_url',
  },
  booklet_premium_express: {
    label: 'Booklet Premium Express',
    innerLabel: 'Inner PDF',
    endpoint: 'montagedpdf-nocover',
    urlField: 'pdf_montaged_nocover_url',
  },
  legacy_premium_express: {
    label: 'Legacy Premium Express',
    innerLabel: 'Inner PDF',
    endpoint: 'readablepdf-nocover',
    urlField: 'pdf_readable_nocover_url',
  },
};
const getExpressTypeConfig = type => (
  EXPRESS_TYPE_CONFIG[type] || EXPRESS_TYPE_CONFIG.booklet_express
);

function ExpressPrintingsPage() {
  const navigate = useNavigate();
  const [generations, setGenerations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingActions, setLoadingActions] = useState({});
  const [error, setError] = useState(null);
  const loadRequestId = useRef(0);

  const loadGenerations = useCallback(async () => {
    const requestId = ++loadRequestId.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await printingsService.getExpressGenerations();
      if (requestId === loadRequestId.current) {
        setGenerations(response.data || []);
      }
    } catch (loadError) {
      if (requestId === loadRequestId.current) {
        setError(loadError.message || 'Failed to load express printings');
      }
    } finally {
      if (requestId === loadRequestId.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGenerations();
    window.addEventListener('expressPrintingsRefresh', loadGenerations);
    return () => {
      window.removeEventListener('expressPrintingsRefresh', loadGenerations);
    };
  }, [loadGenerations]);

  const runAction = async (key, action) => {
    setLoadingActions(previous => ({ ...previous, [key]: true }));
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError.message || 'Express printing action failed');
    } finally {
      setLoadingActions(previous => ({ ...previous, [key]: false }));
    }
  };

  const downloadInnerPdf = generation => {
    const config = getExpressTypeConfig(generation.express_printing_type);
    const key = `${generation._id}:inner`;
    return runAction(key, async () => {
      const response = await fetch(
        `${SERVER_URL}/generations/download/${config.endpoint}/${generation.generation_id}?_id=${generation._id}`,
        { headers: authService.getAuthHeaders() }
      );
      const result = await response.json();
      if (!response.ok || !result.success || !result.data?.[config.urlField]) {
        throw new Error(result.error || `Failed to generate ${config.innerLabel}`);
      }
      const downloadResponse = await fetch(result.data[config.urlField]);
      if (!downloadResponse.ok) throw new Error(`Failed to download ${config.innerLabel}`);
      saveBlobDownload(
        await downloadResponse.blob(),
        result.data.filename || `${generation.generation_id}_${config.innerLabel.replace(' ', '_')}.pdf`
      );
      await loadGenerations();
    });
  };

  const downloadCoverPdf = generation => {
    const key = `${generation._id}:cover`;
    return runAction(key, async () => {
      const response = await fetch(
        `${SERVER_URL}/generations/download/coverpdf/${generation.generation_id}?_id=${generation._id}`,
        { headers: authService.getAuthHeaders() }
      );
      const result = await response.json();
      if (!response.ok || !result.success || !result.data?.pdf_cover_url) {
        throw new Error(result.error || 'Failed to generate Cover PDF');
      }
      const downloadResponse = await fetch(result.data.pdf_cover_url);
      if (!downloadResponse.ok) throw new Error('Failed to download Cover PDF');
      saveBlobDownload(
        await downloadResponse.blob(),
        result.data.filename || `${generation.generation_id}_COVER.pdf`
      );
      await loadGenerations();
    });
  };

  const prepareGeneration = generation => {
    const key = `${generation._id}:prepare`;
    return runAction(key, async () => {
      const result = await printingsService.prepareExpressPrinting([generation._id]);
      navigate(`/printings/${result.printingId}/verify?lane=${result.lane}`);
    });
  };

  const rows = generations.map((generation, index) => {
    const config = getExpressTypeConfig(generation.express_printing_type);
    const needsCover = generation.express_printing_type !== 'booklet_express';
    return (
      <IndexTable.Row id={generation._id} key={generation._id} position={index}>
        <IndexTable.Cell>
          {generation.date ? new Date(generation.date).toLocaleDateString() : '—'}
        </IndexTable.Cell>
        <IndexTable.Cell>{generation.order_id || '—'}</IndexTable.Cell>
        <IndexTable.Cell>{generation.story_slug || '—'}</IndexTable.Cell>
        <IndexTable.Cell>{generation.kid_name || '—'}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge status={needsCover ? 'attention' : 'info'}>{config.label}</Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodySm" as="span">
            {generation.pdfDownloadedAt ? 'Inner ready' : 'Inner pending'}
            {needsCover && ` · ${generation.coverPdfDownloadedAt ? 'Cover ready' : 'Cover pending'}`}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <ButtonGroup segmented>
            <Button
              icon={PrintMinor}
              size="slim"
              loading={loadingActions[`${generation._id}:inner`]}
              onClick={() => downloadInnerPdf(generation)}
            >
              {config.innerLabel}
            </Button>
            {needsCover && (
              <Button
                icon={PrintMinor}
                size="slim"
                loading={loadingActions[`${generation._id}:cover`]}
                onClick={() => downloadCoverPdf(generation)}
              >
                Cover PDF
              </Button>
            )}
            <Button
              size="slim"
              variant="primary"
              loading={loadingActions[`${generation._id}:prepare`]}
              disabled={
                !generation.pdfDownloadedAt
                || (needsCover && !generation.coverPdfDownloadedAt)
              }
              onClick={() => prepareGeneration(generation)}
            >
              Send to Printing
            </Button>
          </ButtonGroup>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Layout>
      <Layout.Section>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </div>
        )}
        <LegacyCard
          title="Express Printings"
          actions={[{
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: loadGenerations,
          }]}
        >
          {isLoading ? (
            <div style={{ padding: '32px', display: 'flex', justifyContent: 'center' }}>
              <Spinner size="large" />
            </div>
          ) : generations.length === 0 ? (
            <EmptyState
              heading="No Express Printings Found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>There are no approved express generations waiting for printing.</p>
            </EmptyState>
          ) : (
            <IndexTable
              resourceName={{ singular: 'generation', plural: 'generations' }}
              itemCount={generations.length}
              selectable={false}
              headings={[
                { title: 'Date' },
                { title: 'Order' },
                { title: 'Story' },
                { title: 'Kid Name' },
                { title: 'Type' },
                { title: 'Files' },
                { title: 'Actions' },
              ]}
            >
              {rows}
            </IndexTable>
          )}
        </LegacyCard>
      </Layout.Section>
    </Layout>
  );
}

export default ExpressPrintingsPage;
